import type { ConsentManagerAdapter } from '@/app/_hooks/use-consent-manager';

/**
 * Cookiebot consent manager adapter.
 *
 * Maps the generic ConsentManagerAdapter interface to Cookiebot's
 * window API and event system.
 */
export const consentManagerAdapter: ConsentManagerAdapter = {
  checkConsent(category) {
    if (typeof window !== 'undefined' && window.Cookiebot?.consent) {
      return window.Cookiebot.consent[category];
    }
    return null;
  },

  openConsentDialog() {
    if (typeof window === 'undefined' || !window.Cookiebot) return;
    window.Cookiebot.renew();

    // Dev-only fallback for localhost.
    // Cookiebot rejects domains that are not whitelisted in the domain group —
    // on `localhost` the dialog therefore never becomes visible and the user
    // is stuck. In development only, if the dialog did not open after 400ms,
    // we grant statistics consent directly so Google Maps can load.
    // The NODE_ENV check is compile-time: the branch is stripped from the
    // production bundle, so Prod GDPR flow stays untouched. If Cookiebot gets
    // whitelisted for localhost later, the dialog opens normally and this
    // fallback never triggers.
    if (
      process.env.NODE_ENV !== 'production' &&
      ['localhost', '127.0.0.1'].includes(window.location.hostname)
    ) {
      setTimeout(() => {
        if (!window.Cookiebot?.dialog?.visible && window.Cookiebot) {
          console.warn(
            '[consent-manager] Cookiebot dialog did not open on localhost. ' +
              'Domain is likely not whitelisted in the Cookiebot Manager. ' +
              'Granting consent locally as a dev-only fallback.'
          );
          // Do NOT call submitCustomConsent — Cookiebot also rejects the
          // server-side logConsent call on unauthorized domains.
          // Instead, mutate the client-side consent object directly and
          // dispatch the Accept event so our subscribers (useConsentManager)
          // pick it up.
          window.Cookiebot.consent = {
            ...window.Cookiebot.consent,
            preferences: true,
            statistics: true,
          };
          window.dispatchEvent(new Event('CookiebotOnAccept'));
        }
      }, 400);
    }
  },

  subscribe(callback) {
    if (typeof window === 'undefined') {
      return () => {
        /* no-op: nothing to unsubscribe on the server */
      };
    }

    window.addEventListener('CookiebotOnConsentReady', callback);
    window.addEventListener('CookiebotOnAccept', callback);
    window.addEventListener('CookiebotOnDecline', callback);

    return () => {
      window.removeEventListener('CookiebotOnConsentReady', callback);
      window.removeEventListener('CookiebotOnAccept', callback);
      window.removeEventListener('CookiebotOnDecline', callback);
    };
  },

  isAvailable() {
    return typeof window !== 'undefined' && !!window.Cookiebot;
  },

  isConfigured() {
    if (typeof document === 'undefined') return false;
    return !!document.getElementById('cookiebot-consent');
  },

  acceptCategory(category) {
    if (typeof window === 'undefined' || !window.Cookiebot?.consent) return;
    // Build consent object with current state + newly accepted category
    const consent = { ...window.Cookiebot.consent, [category]: true };
    window.Cookiebot.submitCustomConsent(
      consent.preferences,
      consent.statistics,
      consent.marketing
    );
  },

  updateLanguage(locale) {
    if (typeof window === 'undefined') return;

    const script = document.getElementById('cookiebot-consent');
    if (script) {
      script.setAttribute('data-culture', locale);
    }

    // Re-open dialog if it's currently visible so the language updates immediately
    if (window.Cookiebot?.dialog?.visible) {
      window.Cookiebot.renew();
    }
  },
};
